/**
 * CMS后台 - 统一素材库
 * 统一管理所有素材：活动照片、CMS上传、家长上传、AI生成等
 */

import { useState, useEffect } from 'react'
import { VITE_API_BASE } from '../config/apiConfig'

const API = VITE_API_BASE

// 拼接完整图片URL
const resolveUrl = (url) => {
  if (!url) return url
  if (url.startsWith('http://') || url.startsWith('https://')) return url
  if (url.startsWith('/')) return 'https://api.boxiaotu.cn' + url
  return '/cms/api/ai-videos/proxy/video?path=' + url
}

const SOURCE_TYPES = [
  { id: 'all', name: '全部' },
  { id: 'activity', name: '活动照片' },
  { id: 'cms', name: 'CMS上传' },
  { id: 'parent', name: '家长上传' },
  { id: 'ai', name: 'AI生成' },
  { id: 'system', name: '系统生成' },
]

const ASSET_TYPES = [
  { id: 'all', name: '全部' },
  { id: 'photo', name: '照片' },
  { id: 'video', name: '视频' },
]

export default function UnifiedAssets() {
  const [assets, setAssets] = useState([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 })
  const [sourceFilter, setSourceFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [childFilter, setChildFilter] = useState('')
  const [children, setChildren] = useState([])
  const [previewAsset, setPreviewAsset] = useState(null)
  const [stats, setStats] = useState(null)
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    loadStats()
    loadChildren()
    loadAssets()
  }, [pagination.page, sourceFilter, typeFilter])

  const loadStats = async () => {
    try {
      const res = await fetch(`${API}/assets/stats/overview`)
      const data = await res.json()
      if (data.success) setStats(data.data)
    } catch (e) {
      console.error('加载统计失败', e)
    }
  }

  const loadChildren = async () => {
    try {
      const res = await fetch(`${API}/children`)
      const data = await res.json()
      if (data.success) setChildren(data.data || [])
    } catch (e) { console.error(e) }
  }

  const loadAssets = async () => {
    setLoading(true)
    try {
      let url = `${API}/assets?page=${pagination.page}&limit=${pagination.limit}`
      if (sourceFilter !== 'all') url += `&source=${sourceFilter}`
      if (typeFilter !== 'all') url += `&asset_type=${typeFilter}`
      if (childFilter) url += `&child_id=${childFilter}`
      
      const res = await fetch(url)
      const data = await res.json()
      if (data.success) {
        setAssets(data.data?.list || data.data || [])
        setPagination(prev => ({ ...prev, total: data.data?.pagination?.total || data.data?.total || 0 }))
      }
    } catch (e) {
      console.error('加载素材失败', e)
    }
    setLoading(false)
  }

  const getChildName = (childId) => {
    const child = children.find(c => c.id === childId)
    return child?.name || childId || '未关联'
  }

  const getSourceTag = (source) => {
    const colors = {
      activity: '#1890ff',
      cms: '#52c41a',
      parent: '#faad14',
      ai: '#722ed1',
      system: '#8c8c8c',
    }
    const names = {
      activity: '活动',
      cms: 'CMS',
      parent: '家长',
      ai: 'AI',
      system: '系统',
    }
    return (
      <span style={{ 
        padding: '2px 8px', 
        borderRadius: 4, 
        fontSize: 11, 
        background: colors[source] || '#8c8c8c',
        color: '#fff'
      }}>
        {names[source] || source}
      </span>
    )
  }

  return (
    <div style={{ padding: 24 }}>
      {/* 页面标题 */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, margin: 0 }}>📦 统一素材库</h1>
        <p style={{ color: '#666', margin: '8px 0 0 0', fontSize: 14 }}>
          统一管理所有素材：活动照片、CMS上传、家长上传、AI生成等
        </p>
      </div>

      {/* 统计卡片 */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 16, marginBottom: 24 }}>
          {[
            { label: '全部素材', value: stats.total || 0, color: '#1890ff' },
            { label: '活动照片', value: stats.by_source?.activity || 0, color: '#1890ff' },
            { label: 'CMS上传', value: stats.by_source?.cms || 0, color: '#52c41a' },
            { label: '家长上传', value: stats.by_source?.parent || 0, color: '#faad14' },
            { label: 'AI生成', value: stats.by_source?.ai || 0, color: '#722ed1' },
            { label: '视频素材', value: stats.video_count || 0, color: '#eb2f96' },
          ].map(s => (
            <div key={s.label} style={{ background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <div style={{ color: '#999', fontSize: 12 }}>{s.label}</div>
              <div style={{ fontSize: 24, color: s.color, fontWeight: 600 }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* 筛选栏 */}
      <div style={{ background: '#fff', borderRadius: 8, padding: 16, marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* 来源筛选 */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: '#666' }}>来源：</span>
            <div style={{ display: 'flex', gap: 4 }}>
              {SOURCE_TYPES.map(s => (
                <button
                  key={s.id}
                  onClick={() => { setSourceFilter(s.id); setPagination(p => ({ ...p, page: 1 })) }}
                  style={{
                    padding: '4px 12px',
                    border: 'none',
                    borderRadius: 4,
                    fontSize: 13,
                    cursor: 'pointer',
                    background: sourceFilter === s.id ? '#1890ff' : '#f0f0f0',
                    color: sourceFilter === s.id ? '#fff' : '#666'
                  }}
                >
                  {s.name}
                </button>
              ))}
            </div>
          </div>

          {/* 类型筛选 */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: '#666' }}>类型：</span>
            <div style={{ display: 'flex', gap: 4 }}>
              {ASSET_TYPES.map(t => (
                <button
                  key={t.id}
                  onClick={() => { setTypeFilter(t.id); setPagination(p => ({ ...p, page: 1 })) }}
                  style={{
                    padding: '4px 12px',
                    border: 'none',
                    borderRadius: 4,
                    fontSize: 13,
                    cursor: 'pointer',
                    background: typeFilter === t.id ? '#1890ff' : '#f0f0f0',
                    color: typeFilter === t.id ? '#fff' : '#666'
                  }}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>

          {/* 孩子筛选 */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: '#666' }}>孩子：</span>
            <select
              value={childFilter}
              onChange={e => { setChildFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })) }}
              style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #d9d9d9', fontSize: 13 }}
            >
              <option value="">全部孩子</option>
              {children.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* 刷新按钮 */}
          <button
            onClick={loadAssets}
            style={{ padding: '4px 12px', border: '1px solid #d9d9d9', borderRadius: 4, background: '#fff', cursor: 'pointer' }}
          >
            🔄 刷新
          </button>
        </div>
      </div>

      {/* 素材列表 */}
      <div style={{ background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>加载中...</div>
        ) : assets.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
            <div style={{ color: '#666' }}>暂无素材</div>
            <div style={{ fontSize: 13, color: '#999', marginTop: 8 }}>
              请在活动照片管理或视频制作中上传素材
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
              {assets.map(asset => (
                <div
                  key={asset.id}
                  onClick={() => setPreviewAsset(asset)}
                  style={{
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: '1px solid #f0f0f0',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {asset.asset_type === 'photo' || asset.url?.match(/\.(jpg|jpeg|png|gif|webp)/i) ? (
                    <img
                      src={resolveUrl(asset.thumbnail || asset.url)}
                      style={{ width: '100%', height: 120, objectFit: 'cover', background: '#f5f5f5' }}
                      onError={e => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="180" height="120"><rect fill="%23f5f5f5" width="180" height="120"/><text x="90" y="65" text-anchor="middle" fill="%23999" font-size="12">图片</text></svg>' }}
                    />
                  ) : asset.asset_type === 'video' || asset.url?.match(/\.(mp4|webm|avi)/i) ? (
                    <div style={{ width: '100%', height: 120, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: 40 }}>🎬</span>
                    </div>
                  ) : (
                    <div style={{ width: '100%', height: 120, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: 40 }}>📄</span>
                    </div>
                  )}
                  <div style={{ padding: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {asset.title || '无标题'}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                      {getSourceTag(asset.source)}
                    </div>
                    <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                      {getChildName(asset.child_id)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* 分页 */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24, gap: 8 }}>
              <button
                disabled={pagination.page <= 1}
                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                style={{ padding: '6px 16px', border: '1px solid #d9d9d9', borderRadius: 4, background: '#fff', cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer' }}
              >
                上一页
              </button>
              <span style={{ padding: '6px 16px' }}>
                第 {pagination.page} 页，共 {Math.ceil(pagination.total / pagination.limit)} 页
              </span>
              <button
                disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                style={{ padding: '6px 16px', border: '1px solid #d9d9d9', borderRadius: 4, background: '#fff', cursor: pagination.page >= Math.ceil(pagination.total / pagination.limit) ? 'not-allowed' : 'pointer' }}
              >
                下一页
              </button>
            </div>
          </>
        )}
      </div>

      {/* 预览弹窗 */}
      {previewAsset && (
        <div
          onClick={() => setPreviewAsset(null)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 8, padding: 24, maxWidth: 600, width: '90%' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>素材详情</h3>
              <button onClick={() => setPreviewAsset(null)} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            {previewAsset.asset_type === 'photo' || previewAsset.url?.match(/\.(jpg|jpeg|png|gif|webp)/i) ? (
              <img
                src={resolveUrl(previewAsset.thumbnail || previewAsset.url)}
                style={{ width: '100%', maxHeight: 400, objectFit: 'contain', borderRadius: 4, background: '#f5f5f5' }}
                onError={e => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="%23f5f5f5" width="200" height="200"/><text x="100" y="105" text-anchor="middle" fill="%23999" font-size="14">图片加载失败</text></svg>' }}
              />
            ) : (
              <div style={{ height: 200, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4 }}>
                <span style={{ fontSize: 48 }}>🎬</span>
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              <div style={{ marginBottom: 8 }}>
                <strong>标题：</strong>{previewAsset.title || '无标题'}
              </div>
              <div style={{ marginBottom: 8 }}>
                <strong>来源：</strong>{getSourceTag(previewAsset.source)}
              </div>
              <div style={{ marginBottom: 8 }}>
                <strong>关联孩子：</strong>{getChildName(previewAsset.child_id)}
              </div>
              <div style={{ marginBottom: 8 }}>
                <strong>来源ID：</strong>{previewAsset.source_id || '-'}
              </div>
              <div style={{ marginBottom: 8 }}>
                <strong>ID：</strong>
                <span style={{ fontSize: 11, color: '#999' }}>{previewAsset.id}</span>
              </div>
              <div style={{ marginBottom: 8 }}>
                <strong>URL：</strong>
                <a href={previewAsset.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, wordBreak: 'break-all' }}>
                  {previewAsset.url}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
